import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { isMagicLink, signInWithMagicLink } from '../services/auth';
import { useToast } from '../store/ToastContext';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { Input } from '../components/Input';
import { Button } from '../components/Button';

export const MagicLinkHandler: React.FC = () => {
    const [loading, setLoading] = useState(true);
    const [email, setEmail] = useState('');
    const [needsEmail, setNeedsEmail] = useState(false);
    const navigate = useNavigate();
    const { showToast } = useToast();

    const handleSignIn = useCallback(async (signInEmail: string, url: string) => {
        setLoading(true);
        try {
            await signInWithMagicLink(signInEmail, url);
            window.localStorage.removeItem('evenly_magic_link_email');
            showToast('Successfully signed in!', 'success');
            void navigate('/tabs');
        } catch (error) {
            console.error('Error signing in with magic link:', error);
            showToast('Failed to sign in. The link may have expired.', 'error');
            void navigate('/tabs');
        }
    }, [navigate, showToast]);

    useEffect(() => {
        const url = window.location.href;
        
        if (!isMagicLink(url)) {
            showToast('Invalid magic link.', 'error');
            void navigate('/tabs');
            return;
        }

        const savedEmail = window.localStorage.getItem('evenly_magic_link_email');
        if (savedEmail) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            void handleSignIn(savedEmail, url);
        } else {
            setNeedsEmail(true);
            setLoading(false);
        }
    }, [navigate, showToast, handleSignIn]); 

    const handleSubmitEmail = (e: React.SyntheticEvent) => {
        e.preventDefault();
        if (!email.trim()) return;
        void handleSignIn(email, window.location.href);
    };

    if (loading) {
        return (
            <div className="flex h-screen w-full items-center justify-center bg-bg-app">
                <div className="flex flex-col items-center gap-4">
                    <LoadingSpinner />
                    <p className="text-text-muted">Authenticating...</p>
                </div>
            </div>
        );
    }

    if (needsEmail) {
        return (
            <div className="flex h-screen w-full items-center justify-center bg-bg-app p-4">
                <div className="w-full max-w-md bg-bg-card p-6 rounded-xl shadow-sm border border-border-default">
                    <h2 className="text-xl font-bold text-text-heading mb-2">Confirm Your Email</h2>
                    <p className="text-sm text-text-muted mb-6">
                        You opened this link on a different device or browser. Please confirm your email address to continue.
                    </p>
                    <form onSubmit={handleSubmitEmail} className="space-y-4">
                        <Input
                            id="confirm-email"
                            label="Email Address"
                            type="email"
                            placeholder="email@example.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                        />
                        <Button type="submit" className="w-full">
                            Sign In
                        </Button>
                    </form>
                </div>
            </div>
        );
    }

    return null;
};
